import { useState } from 'react';
import { AlertTriangle, ChevronRight, Clock, Gauge, Info, Layers, X } from 'lucide-react';
import { DIRECTIONAL_BASELINE_LABEL } from '@siliconx/shared';
import type { ClientFmvSnapshot } from '../asset-detail/asset-detail-types';

/**
 * SIL-334 — the FMV tile's "show the math" affordance. DESIGN.md names the
 * Calculation Basis Drawer (`apps/customer/src/components/financials/
 * CalculationBasisDrawer.tsx`) as this product's signature "where did this
 * number come from" surface, so this reuses its exact visual contract (right
 * -hand overlay, max-w-md/448px, shadow-xl, bg-black/40 scrim, uppercase
 * eyebrow + title, amber fallback callout BEFORE the inputs table, then
 * inputs table, then formula reference).
 *
 * It is a purpose-built sibling rather than a literal reuse of
 * `CalculationBasisTrigger`/`CalculationBasisDrawer` because that component's
 * `CalculationBasisInput` DTO is numeric-value-only (financials figures) —
 * an FMV snapshot's real basis fields (marketability tier letter, value
 * basis, comp recency) are strings/derived signals, not numbers. Rather than
 * force categorical data through a numeric schema (fabricating a numeric
 * encoding of a tier letter would be exactly the kind of invented figure
 * this feature exists to prevent), this renders the fields
 * `ClientFmvSnapshot` genuinely carries today, in the same shape.
 *
 * SIL-926 rewired this against `ClientFmvSnapshot` — the shape
 * GET /api/client/assets/:assetId's snapshot history actually returns —
 * instead of the ops-audience `AssetSnapshotHistoryItem`. That was a
 * pre-existing bug: this component was typed against fields (fmvLowUsd/
 * fmvHighUsd, triggerName, modelVersion) the client route never populated,
 * some of which (the fmvLowUsd/fmvHighUsd band) the customer role is
 * deliberately never granted (578/582 doctrine — customers see the FMV
 * median only). SIL-926 both fixes that mismatch and adds three new
 * disclosures: value basis (baseline price source + sample size), comp
 * recency (newest qualifying comp's age), and a stale-data callout.
 */

const MARKETABILITY_LABEL: Record<ClientFmvSnapshot['marketabilityTier'], string> = {
  A: 'Strong — deep, recent comps',
  B: 'Moderate — adequate comps',
  C: 'Thin — limited comps',
};

/** Info-glyph trigger, same shape as the shared `CalculationBasisTrigger` —
 * owns its own open/close state so the FMV tile can drop it in one line.
 * Renders nothing when there's no snapshot to explain. */
export function FmvBasisTrigger({ snapshot }: { snapshot: ClientFmvSnapshot | null }) {
  const [open, setOpen] = useState(false);

  if (!snapshot) return null;

  const flagged =
    snapshot.confidenceScore < 50 ||
    snapshot.marketabilityTier === 'C' ||
    snapshot.staleDataException;

  return (
    <>
      <button
        type="button"
        aria-label="Show the math for Fair Market Value"
        onClick={() => setOpen(true)}
        className={`inline-flex items-center justify-center rounded-full p-0.5 ${
          flagged ? 'text-amber-500 hover:text-amber-600' : 'text-zinc-400 hover:text-zinc-600'
        }`}
        title="Show the math"
      >
        <Info className="size-3.5" />
      </button>
      {open && <FmvBasisDrawer snapshot={snapshot} onClose={() => setOpen(false)} />}
    </>
  );
}

/**
 * SIL-920 — always-visible confidence/marketability/comp-recency strip,
 * picked over `FmvBasisTrigger`'s bare info glyph for the FMV scorecard
 * tile (5 variants prototyped; this one — a stat row that discloses the
 * measurements without a click — read clearest against the other tiles on
 * the same row). `FmvBasisTrigger` stays in place on the Value tab's FMV
 * history card, a denser context where the compact glyph still earns its
 * keep. Renders nothing when there's no snapshot to explain. */
export function FmvConfidenceStrip({ snapshot }: { snapshot: ClientFmvSnapshot | null }) {
  const [open, setOpen] = useState(false);

  if (!snapshot) return null;

  const flagged =
    snapshot.confidenceScore < 50 ||
    snapshot.marketabilityTier === 'C' ||
    snapshot.staleDataException;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Show the math for Fair Market Value"
        className={`mt-2.5 flex w-full items-center justify-between rounded-md border px-2.5 py-1.5 text-left ${
          flagged ? 'border-amber-200 bg-amber-50' : 'border-emerald-200 bg-emerald-50'
        }`}
      >
        <span className="flex items-center gap-1 text-xs font-medium text-zinc-700">
          <Gauge className="size-3" /> {snapshot.confidenceScore}%
        </span>
        <span className="flex items-center gap-1 text-xs font-medium text-zinc-700">
          <Layers className="size-3" /> Tier {snapshot.marketabilityTier}
        </span>
        <span className="flex items-center gap-1 text-xs font-medium text-zinc-700">
          <Clock className="size-3" />
          {snapshot.isDirectionalBaseline
            ? 'Directional'
            : snapshot.compRecencyDays != null
              ? `${snapshot.compRecencyDays}d`
              : 'n/a'}
        </span>
        <ChevronRight className={`size-3 ${flagged ? 'text-amber-700' : 'text-emerald-700'}`} />
      </button>
      {open && <FmvBasisDrawer snapshot={snapshot} onClose={() => setOpen(false)} />}
    </>
  );
}

export function FmvBasisDrawer({
  snapshot,
  onClose,
}: {
  snapshot: ClientFmvSnapshot;
  onClose: () => void;
}) {
  const fallbacks: string[] = [];
  // SIL-1828 (R2): a virtual baseline is not a market price. Say so first,
  // and (below) suppress every market-comp disclosure for it.
  const directional = snapshot.isDirectionalBaseline;
  if (directional) {
    fallbacks.push(
      `${DIRECTIONAL_BASELINE_LABEL} - no market comps exist yet for this hardware, so this figure is built from component prices. Treat it as directional, not exact.`,
    );
  }
  if (snapshot.marketabilityTier === 'C' && !directional) {
    fallbacks.push('Marketability — insufficient market depth, estimate widened.');
  }
  if (snapshot.confidenceScore < 50) {
    fallbacks.push('Confidence — based on limited signal; treat this figure as directional, not exact.');
  }
  // SIL-926: stale-data exception — the market comps behind a 'sku_mcmp'
  // baseline are either absent or older than the source's freshness
  // threshold. Matches the existing amber fallback pattern above rather than
  // inventing a second callout style.
  if (snapshot.staleDataException && !directional) {
    fallbacks.push(
      snapshot.compRecencyDays != null
        ? `Stale comps — the freshest comp behind this price is ${snapshot.compRecencyDays} days old, past this source's freshness window. Treat this figure as directional, not exact.`
        : 'Stale comps — no recent market comps were found for this baseline. Treat this figure as directional, not exact.',
    );
  }

  const asOf = new Date(snapshot.pricedAt).toLocaleDateString();
  const compAsOf = snapshot.compNewestObservedAt
    ? new Date(snapshot.compNewestObservedAt).toLocaleDateString()
    : null;

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/40" onClick={onClose}>
      <div
        role="dialog"
        aria-label="Show the math — Fair Market Value"
        className="h-full w-full max-w-md overflow-y-auto bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-zinc-500">
              Show the math
            </p>
            <h2 className="mt-0.5 text-base font-semibold text-zinc-900">Fair Market Value</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-zinc-500 hover:bg-zinc-100"
          >
            <X className="size-4" />
          </button>
        </div>

        {/* Candid-fallback callout — every fallback-derived input, in plain
            language, BEFORE anything else. Never softened or moved below
            the fold (DESIGN.md's Calculation Basis Drawer spec). */}
        {fallbacks.length > 0 && (
          <div className="mt-4 flex items-start gap-2.5 rounded-lg border border-amber-300 bg-amber-50 px-3.5 py-3">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600" />
            <div className="space-y-1 text-sm text-amber-900">
              {fallbacks.map((f) => (
                <p key={f}>{f}</p>
              ))}
            </div>
          </div>
        )}

        {/* Inputs table */}
        <div className="mt-5">
          <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Inputs</p>
          <table className="mt-2 w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-semibold uppercase tracking-wider text-zinc-400">
                <th className="py-1 pr-2">Input</th>
                <th className="py-1 pr-2">Value</th>
                <th className="py-1 pr-2">Source</th>
                <th className="py-1">As of</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              <tr>
                <td className="py-2 pr-2 align-top font-medium text-zinc-700">FMV (median)</td>
                <td className="py-2 pr-2 align-top tabular-nums text-zinc-700">
                  {snapshot.fmvMedUsd.toLocaleString('en-US', {
                    style: 'currency',
                    currency: 'USD',
                  })}
                  {directional && (
                    <span className="ml-1 inline-flex items-center rounded-full bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-700">
                      Directional
                    </span>
                  )}
                </td>
                <td className="py-2 pr-2 align-top text-zinc-500">FMV snapshot</td>
                <td className="py-2 align-top text-zinc-400">{asOf}</td>
              </tr>
              <tr>
                <td className="py-2 pr-2 align-top font-medium text-zinc-700">Marketability</td>
                <td
                  className={`py-2 pr-2 align-top ${
                    snapshot.marketabilityTier === 'C' ? 'text-amber-700' : 'text-zinc-700'
                  }`}
                >
                  {directional
                    ? `Tier ${snapshot.marketabilityTier}`
                    : `Tier ${snapshot.marketabilityTier} — ${MARKETABILITY_LABEL[snapshot.marketabilityTier]}`}
                </td>
                <td className="py-2 pr-2 align-top text-zinc-500">
                  {directional ? 'Pricing engine' : 'Market comps'}
                  {snapshot.marketabilityTier === 'C' && (
                    <span className="ml-1 inline-flex items-center rounded-full bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-700">
                      estimated
                    </span>
                  )}
                </td>
                <td className="py-2 align-top text-zinc-400">{asOf}</td>
              </tr>
              <tr>
                <td className="py-2 pr-2 align-top font-medium text-zinc-700">Confidence score</td>
                <td
                  className={`py-2 pr-2 align-top tabular-nums ${
                    snapshot.confidenceScore < 50 ? 'text-amber-700' : 'text-zinc-700'
                  }`}
                >
                  {snapshot.confidenceScore}/100
                  {snapshot.confidenceScore < 50 && (
                    <span className="ml-1 inline-flex items-center rounded-full bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-700">
                      estimated
                    </span>
                  )}
                </td>
                <td className="py-2 pr-2 align-top text-zinc-500">FMV snapshot</td>
                <td className="py-2 align-top text-zinc-400">{asOf}</td>
              </tr>
              {/* SIL-926: value basis — baseline price source + sample size. */}
              <tr>
                <td className="py-2 pr-2 align-top font-medium text-zinc-700">Value basis</td>
                <td className="py-2 pr-2 align-top text-zinc-700">
                  {directional ? DIRECTIONAL_BASELINE_LABEL : snapshot.baselinePriceSourceName}
                  {!directional && snapshot.baselinePriceSampleSize != null && (
                    <span className="text-zinc-500">
                      {' '}
                      · {snapshot.baselinePriceSampleSize} comp
                      {snapshot.baselinePriceSampleSize === 1 ? '' : 's'}
                    </span>
                  )}
                </td>
                <td className="py-2 pr-2 align-top text-zinc-500">Pricing engine</td>
                <td className="py-2 align-top text-zinc-400">{asOf}</td>
              </tr>
              {/* SIL-926: comp recency — only meaningful (and only present)
                  for a 'sku_mcmp' baseline with a resolved comp; a null
                  compRecencyDays means either the baseline isn't comp-backed
                  or no qualifying comp was found (see the stale-data callout
                  above for the latter case), so the row is omitted rather
                  than shown with a fabricated value. */}
              {!directional && snapshot.compRecencyDays != null && (
                <tr>
                  <td className="py-2 pr-2 align-top font-medium text-zinc-700">Comp recency</td>
                  <td
                    className={`py-2 pr-2 align-top ${
                      snapshot.staleDataException ? 'text-amber-700' : 'text-zinc-700'
                    }`}
                  >
                    Based on comps as recent as {snapshot.compRecencyDays}{' '}
                    day{snapshot.compRecencyDays === 1 ? '' : 's'} ago
                    {snapshot.staleDataException && (
                      <span className="ml-1 inline-flex items-center rounded-full bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-700">
                        stale
                      </span>
                    )}
                  </td>
                  <td className="py-2 pr-2 align-top text-zinc-500">Market comps</td>
                  <td className="py-2 align-top text-zinc-400">{compAsOf}</td>
                </tr>
              )}
              {/* SIL-953: ask->executed calibration disclosure. Comp-based
                  pricing is asking-price-dominated; where a published
                  class-level executed-price factor exists, say so
                  explicitly — this is a MODELLED correction, never the
                  same thing as an observed comp price, and must never
                  render identically to one. */}
              {!directional && snapshot.isModelled && (
                <tr>
                  <td className="py-2 pr-2 align-top font-medium text-zinc-700">
                    Ask vs. executed
                  </td>
                  <td className="py-2 pr-2 align-top text-zinc-700">
                    Executed sales for this class run{' '}
                    {snapshot.askExecutedMultiplier != null
                      ? `~${Math.round(Math.abs(1 - snapshot.askExecutedMultiplier) * 100)}%`
                      : null}{' '}
                    {snapshot.askExecutedMultiplier != null && snapshot.askExecutedMultiplier < 1
                      ? 'below'
                      : 'above'}{' '}
                    posted asks (n={snapshot.askExecutedNPairs})
                    <span className="ml-1 inline-flex items-center rounded-full bg-blue-100 px-1.5 py-0.5 text-xs font-medium text-blue-700">
                      modelled
                    </span>
                  </td>
                  <td className="py-2 pr-2 align-top text-zinc-500">Ask/executed calibration</td>
                  <td className="py-2 align-top text-zinc-400">{asOf}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Formula reference */}
        <div className="mt-5 border-t border-zinc-100 pt-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Formula</p>
          <code className="mt-1.5 block break-all rounded-md bg-zinc-50 px-2.5 py-1.5 font-mono text-xs text-zinc-600">
            baseline × age × config × condition × marketability
          </code>
          <p className="mt-1.5 text-xs text-zinc-500">
            Baseline, age, config, and condition multipliers are internal to the pricing engine;
            value basis, marketability, and confidence (above) are the factors this snapshot
            discloses.
          </p>
        </div>
      </div>
    </div>
  );
}
